import { animate, state, style, transition, trigger } from '@angular/animations';
import { ChangeDetectorRef, Component, Injector, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import LayoutService from '@app/services/layout.service';
import SidebarService from '@app/services/sidebar.service';
import { AppConsts } from '@shared/AppConsts';
import AppComponentBase from '@shared/common/app-component-base';
import { UniqueObjectSet } from '@shared/helpers/UniqueObjectSet';
import { TenantServiceProxy } from '@shared/service-proxies/service-proxies';
import LocalStorageService from '@shared/utils/local-storage.service';
import { PermissionCheckerService } from 'abp-ng2-module';
import { isNil } from 'lodash';
import { filter } from 'rxjs/operators';

import AppMenuItem from './app-menu-item';

const expandAnimationStartStateName = 'small';
const expandAnimationEndStateName = 'big';
const sidebarDefaultWidth = 60;
const sidebarExpandedWidth = 205;
const animationTime = 300;

@Component({
    selector: 'app-sidebar',
    templateUrl: './sidebar.component.html',
    styleUrls: ['./sidebar.component.less'],
    encapsulation: ViewEncapsulation.None,
    animations: [
        trigger('expand', [
            state(expandAnimationStartStateName, style({ width: sidebarDefaultWidth })),
            state(expandAnimationEndStateName, style({ width: sidebarExpandedWidth })),
            transition('small <=> big', animate(animationTime + 'ms ease-in-out')),
        ]),
    ],
    standalone: false,
})
class SidebarComponent extends AppComponentBase implements OnInit {
    @Input() iconMenu = false;

    @Input() menuClass = 'menu menu-column menu-rounded px-3';

    @Input() isDocked!: boolean;

    logoUrl = '';

    smallLogoUrl = '';

    defaultLogoUrl = AppConsts.CosherBigLogoPath;

    defaultSmallLogoUrl = AppConsts.CosherSmallLogoPath;

    currentRouteUrl = '';

    openAccordions: UniqueObjectSet<AppMenuItem> = new UniqueObjectSet<AppMenuItem>();

    isExpanded: boolean;

    expandAnimStart = expandAnimationStartStateName;

    expandAnimEnd = expandAnimationEndStateName;

    private lastAnimationTime = 0;

    private animationCompletionBreakpoint = 90;

    private _sidebarStateStorageKey = 'sidebar-docked-state';

    private isMobile = false;

    private canSidebarBeDocked = false;

    constructor(
        injector: Injector,
        private router: Router,
        private sidebarService: SidebarService,
        public permission: PermissionCheckerService,
        private layoutService: LayoutService,
        private localStorageService: LocalStorageService,
        private tenantService: TenantServiceProxy,
        private cd: ChangeDetectorRef,
    ) {
        super(injector);
    }

    ngOnInit() {
        this.currentRouteUrl = this.router.url.split(/[?#]/)[0];
        this.router.events
            .pipe(filter((event) => event instanceof NavigationEnd))
            .subscribe(() => this.currentRouteUrl = this.router.url.split(/[?#]/)[0]);

        this.sidebarService.getSidebarState()
            .subscribe((value) => {
                this.isExpanded = value;
            });

        this.layoutService.isMobile$.subscribe(isTabletOrLess => {
            this.isMobile = isTabletOrLess;
        });

        this.layoutService.canSidebarBeDocked$.subscribe(canBeDocked => {
            this.canSidebarBeDocked = canBeDocked;
        });

        this.observeSidebarDockedState();

        this.getLogos();
    }

    getLogos(): void {
        if (!isNil(abp.session.tenantId)) {
            this.tenantService
                .getDetails(abp.session.tenantId)
                .subscribe(({ logoUrl, smallLogoUrl }) => {
                    this.logoUrl = logoUrl;
                    this.smallLogoUrl = smallLogoUrl;
                });
        } else {
            const { defaultLogoUrl, defaultSmallLogoUrl } = this;
            this.logoUrl = defaultLogoUrl;
            this.smallLogoUrl = defaultSmallLogoUrl;
        }
    }

    onMouseLeaveSidebar(): void {
        if (this.isDocked) {
            return;
        }

        this.sidebarService.closeSidebar();

        this.openAccordions.forEach(item => {
            item.expanded = false;
        });
    }

    onMouseEnterSidebar() {
        this.sidebarService.showSidebar();
    }

    toggleSidebarDock() {
        this.layoutService.toggleFixedSidebar();

        this.setStoredSidebarState(!this.isDocked);
    }

    hideSidebar(): void {
        if (this.isDocked) {
            return;
        }
        this.sidebarService.closeSidebar();
    }

    /*
        Calculate far along the sidebar animation is. If < 90%, do not open accordions.
        this.animationCompletionBreakpoint is what determines when the animation counts
        as complete
     */
    getAnimationTimeSpentPercentage(): number {
        const currentTime = performance.now();
        let percentage = 0;
        if (this.lastAnimationTime !== 0) {
            const timeDifference = Math.round(currentTime - this.lastAnimationTime);
            percentage = timeDifference / animationTime * 100;
            percentage = percentage > 100
                ? 100
                : percentage;
        }
        this.lastAnimationTime = currentTime;
        return percentage;
    }

    animationEventHandler(event: any) {
        if (event.fromState === 'void') {
            return;
        }
        const animationPercentage = this.getAnimationTimeSpentPercentage();
        if (animationPercentage < this.animationCompletionBreakpoint) {
            return;
        }
        if (event.toState === this.expandAnimEnd) {
            this.openAccordions.forEach(item => {
                item.expanded = true;
            });
        } else if (event.toState === this.expandAnimStart) {
            this.openAccordions.forEach(item => {
                item.expanded = false;
            });
        }
        this.lastAnimationTime = 0;
        this.cd.detectChanges();
    }

    observeSidebarDockedState() {
        this.localStorageService.getItem<any>(this._sidebarStateStorageKey)
            .subscribe({
                next: (sidebarState) => {
                    if (sidebarState && this.canSidebarBeDocked) {
                        this.dockSidebarIfStored();
                    } else {
                        this.layoutService.undockSidebar();
                    }
                },
                error: (error) => {
                    console.error('Failed to retrieve item:', error);
                },
            });
    }

    dockSidebarIfStored(): void {
        this.layoutService.dockSidebar();
        this.sidebarService.showSidebar();
    }

    onLogoClick(): void {
        if (this.isMobile) {
            this.sidebarService.closeSidebar();
        }
    }

    async setStoredSidebarState(sidebarDockedState): Promise<void> {
        this.localStorageService.setItem(this._sidebarStateStorageKey, sidebarDockedState)
            .subscribe();
    }
}

export default SidebarComponent;
